import { isLetterOrPlaceholderAvatarUrl, resolveImageUrl } from "./profileImage";
import { getStoragePublicUrl, isSupabaseConfigured } from "../lib/supabaseClient";
import { isSupabaseBackend } from "../services/config";

const PLACEHOLDER_VALUES = new Set(["string", "null", "undefined", "n/a", "na", "none", "test"]);
const AZURE_STORAGE_BASE =
  process.env.REACT_APP_Image_Url || "https://payinapoapistorage.blob.core.windows.net/images/";

export function isPlaceholderText(value) {
  if (value == null) return true;
  const text = String(value).trim().toLowerCase();
  return !text || PLACEHOLDER_VALUES.has(text);
}

export { isLetterOrPlaceholderAvatarUrl };

export function isBuggyImageUri(uri) {
  if (!uri || typeof uri !== "string") return true;
  const lower = uri.trim().toLowerCase();
  if (!lower) return true;
  if (lower.includes("/images/string") || lower.endsWith("/images/")) return true;
  if (lower.includes("placeholder") || lower.includes("via.placeholder")) return true;
  if (PLACEHOLDER_VALUES.has(lower.replace(/^https?:\/\/.*\//, ""))) return true;
  return false;
}

/** Turn a stored path or absolute URI into a browser-loadable URL. */
export function resolveExplorePhotoUrl(uri) {
  if (!uri || typeof uri !== "string") return null;
  const trimmed = uri.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  if (isSupabaseBackend() && isSupabaseConfigured) {
    const fromStorage = getStoragePublicUrl(trimmed);
    if (fromStorage) return fromStorage;
  }
  return `${AZURE_STORAGE_BASE}${trimmed.replace(/^\//, "")}`;
}

/** Gallery + primary photo URLs Discover can actually render (not letter/OAuth placeholders). */
export function collectExplorePhotoUrls(entry) {
  const user = entry?.user ?? entry;
  const images = entry?.images ?? user?.images ?? [];
  const urls = [];
  const seen = new Set();

  const push = (url) => {
    const resolved = resolveExplorePhotoUrl(url);
    if (!resolved) return;
    if (isLetterOrPlaceholderAvatarUrl(resolved) || isBuggyImageUri(resolved)) return;
    if (seen.has(resolved)) return;
    seen.add(resolved);
    urls.push(resolved);
  };

  for (const img of images) {
    push(resolveImageUrl(img));
  }
  // Some complete profiles only have a real photo on profileImageUri (no gallery rows).
  push(user?.profileImageUri ?? user?.profile_image_uri ?? null);

  return urls;
}

export function profileHasDisplayablePhoto(entry) {
  return collectExplorePhotoUrls(entry).length > 0;
}

/** Normalize gender values stored in preferences / legacy fields */
export function normalizeGender(value) {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "male" || text === "m") return "male";
  if (text === "female" || text === "f") return "female";
  return null;
}

/** Opposite gender only: male ↔ female */
export function isOppositeGender(viewerGender, candidateGender) {
  const viewer = normalizeGender(viewerGender);
  const candidate = normalizeGender(candidateGender);
  if (!viewer || !candidate) return false;
  return (
    (viewer === "male" && candidate === "female") ||
    (viewer === "female" && candidate === "male")
  );
}

/** Profiles safe to show in explore — real name + at least one resolvable gallery photo */
export function isValidExploreProfile(entry) {
  const user = entry?.user;
  if (!user?.id) return false;
  if (isPlaceholderText(user.firstName)) return false;
  if (!profileHasDisplayablePhoto(entry)) return false;
  return true;
}

export function filterExploreProfiles(list, viewerGender = null) {
  if (!Array.isArray(list)) return [];
  const gender = normalizeGender(viewerGender);
  return list.filter((entry) => {
    if (!isValidExploreProfile(entry)) return false;
    if (!gender) return true;
    return isOppositeGender(gender, entry?.user?.gender ?? entry?.user?.preferences?.gender);
  });
}
